<template>
  <div>
    <app-head :title="$page.props?.meta_title || $page.props?.promo?.name || $page.props?.book?.title || title" />

    <jet-banner />

    <div class="min-h-screen">
      <nav class="bg-white dark:bg-primary-darkest/75 dark:text-white shadow-lg">
        <!-- Primary Navigation Menu -->
        <div class="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div class="flex justify-between h-gr-double-6 md:h-gr-double-7 leading-gr-7">
            <div class="flex">
              <!-- Logo -->
              <div class="shrink-0 flex items-center">
                <subdomain-link :href="route('home')">
                  <jet-application-mark class="block" />
                </subdomain-link>
              </div>

              <!-- Navigation Links -->
              <div class="hidden space-x-gr-5 lg:space-x-gr-6 md:-my-px md:ml-gr-6 lg:ml-gr-7 md:flex text-sm lg:text-base">
                <jet-nav-link root :href="route('dashboard')" :active="route().current('dashboard')">
                  <i class="fa fa-fw fa-th mr-gr-3"></i>Dashboard
                </jet-nav-link>
                <jet-nav-link root :href="route('admin.books')" :active="route().current('admin.books')">
                  <i class="fa fa-fw fa-books mr-gr-3"></i>Books
                </jet-nav-link>
                <jet-nav-link root
                  :href="route('admin.promos')"
                  :active="route().current('admin.promos') && !route().params?.b"
                >
                  <i class="fa fa-fw fa-headphones mr-gr-3"></i>Promos
                </jet-nav-link>
                <jet-nav-link root
                  v-if="$page.props.team?.can_import"
                  :href="route('admin.imports')"
                  :active="route().current('admin.imports')"
                >
                  <i class="fa fa-fw fa-file-spreadsheet mr-gr-3"></i>Imports
                </jet-nav-link>
              </div>
            </div>

            <div class="flex md:items-center md:ml-6">
              <div class="hidden md:block ml-3 relative">
                <!-- Teams Dropdown -->
                <jet-dropdown
                  align="right"
                  width="60"
                  v-if="$page.props?.jetstream?.hasTeamFeatures && $page.props.user.current_team"
                >
                  <template #trigger>
                    <span class="inline-flex rounded-md">
                      <button
                        type="button"
                        class="
                          font-bold
                          inline-flex
                          items-center
                          px-3
                          py-2
                          border border-transparent
                          text-sm
                          leading-4
                          font-medium
                          rounded-md
                          text-gray-500
                          bg-white
                          hover:bg-gray-50
                          hover:text-gray-700
                          focus:outline-none
                          focus:bg-gray-50
                          active:bg-gray-50
                          dark:bg-black
                          dark:text-white
                          transition
                          ease-in-out
                          duration-150
                        "
                      >
                        {{ $page.props.user.current_team.name }}

                        <svg
                          class="ml-2 -mr-0.5 h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>
                    </span>
                  </template>

                  <template #content>
                    <div class="w-60">
                      <!-- Team Management -->
                      <template v-if="$page.props.jetstream.hasTeamFeatures">
                        <div class="dropdown-heading">Manage Team</div>

                        <!-- Team Settings -->
                        <jet-dropdown-link
                          root
                          :href="route('teams.show', $page.props.user.current_team)"
                        >
                          Team Settings
                        </jet-dropdown-link>

                        <!-- Billing -->
                        <a
                          v-if="$page.props.canAccessBilling"
                          :href="route('spark.portal')"
                          class="dropdown-link"
                          target="_blank"
                          rel="noopener"
                        >
                          Team Billing
                        </a>

                        <jet-dropdown-link
                          root
                          :href="route('teams.create')"
                          v-if="$page.props.jetstream.canCreateTeams"
                        >
                          Create New Team
                        </jet-dropdown-link>

                        <div v-if="teamCount > 1" class="dropdown-hr"></div>

                        <!-- Team Switcher -->
                        <div v-if="teamCount > 1" class="dropdown-heading">Switch Teams</div>

                        <template
                          v-if="teamCount > 1"
                          v-for="team in $page.props.user.all_teams"
                          :key="team.id"
                        >
                          <form @submit.prevent="switchToTeam(team)">
                            <jet-dropdown-link as="button">
                              <div class="flex items-center">
                                <svg
                                  v-if="team.id == $page.props.user.current_team_id"
                                  class="mr-2 h-5 w-5 text-tertiary-400"
                                  fill="none"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                <div>{{ team.name }}</div>
                              </div>
                            </jet-dropdown-link>
                          </form>
                        </template>
                      </template>
                    </div>
                  </template>
                </jet-dropdown>
              </div>

              <profile-nav class="hidden md:block ml-3" />

              <!-- Hamburger -->
              <div class="-mr-2 flex items-center md:hidden">
                <button
                  @click="showingNavigationDropdown = !showingNavigationDropdown"
                  class="
                    inline-flex
                    items-center
                    justify-center
                    p-2
                    rounded-md
                    text-gray-400
                    hover:text-gray-500
                    hover:bg-gray-100
                    dark:text-gray-600
                    dark:hover:text-gray-500
                    dark:hover:bg-gray-800
                    focus:outline-none
                    focus:bg-gray-100
                    focus:text-gray-500
                    dark:focus:bg-gray-900
                    dark:focus:text-gray-500
                    transition
                    duration-150
                    ease-in-out
                  "
                >
                  <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                    <path
                      :class="{
                        hidden: showingNavigationDropdown,
                        'inline-flex': !showingNavigationDropdown,
                      }"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                    <path
                      :class="{
                        hidden: !showingNavigationDropdown,
                        'inline-flex': showingNavigationDropdown,
                      }"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Responsive Navigation Menu -->
          <div
            :class="{
              block: showingNavigationDropdown,
              hidden: !showingNavigationDropdown,
            }"
            class="md:hidden"
          >
            <profile-nav-links />
          </div>
        </div>
      </nav>

      <!-- Page Heading -->
      <header v-if="$slots.header" class="bg-white dark:bg-black dark:text-white shadow">
        <div class="max-w-7xl mx-auto py-6 px-4 md:px-6 lg:px-8">
          <slot name="header"></slot>
        </div>
      </header>

      <!-- Page Content -->
      <main>
        <breadcrumbs-nav :class="breadcrumbsClasses" />
        <slot v-bind="$attrs"></slot>
      </main>

      <!-- Page Footer -->
      <app-footer class="text-xs mt-gr-8" />
    </div>
  </div>
</template>

<script>
  import ProfileNav from '@/Components/ProfileNav';
  import ProfileNavLinks from '@/Components/ProfileNavLinks';
  import AppFooter from '@/Components/AppFooter';
  import AppHead from '@/Components/AppHead';
  import BreadcrumbsNav from '@/Components/BreadcrumbsNav';
  import JetApplicationMark from '@/Jetstream/ApplicationMark';
  import JetBanner from '@/Jetstream/Banner';
  import JetDropdown from '@/Jetstream/Dropdown';
  import JetDropdownLink from '@/Jetstream/DropdownLink';
  import JetNavLink from '@/Jetstream/NavLink';

  export default {
    props: {
      breadcrumbsClasses: {
        type: String,
        default: '',
      },
      title: {
        type: String,
        default: 'ONE Audiobooks',
      },
    },


    components: {
      ProfileNav,
      ProfileNavLinks,
      AppHead,
      AppFooter,
      BreadcrumbsNav,
      JetApplicationMark,
      JetBanner,
      JetDropdown,
      JetDropdownLink,
      JetNavLink,
    },

    data() {
      return {
        showingNavigationDropdown: false,
      };
    },

    computed: {
      teamCount() {
        return Object.keys(this.$page.props?.user?.all_teams || {}).length;
      },
    },

    methods: {
      switchToTeam(team) {
        let redirect = route(route()?.current(), {...route()?.params});
        this.$inertia.put(
          route('current-team.update'),
          {
            team_id: team.id,
            redirect,
          },
          {
            preserveState: false,
          },
        );
      },
    },
  };
</script>
